"use client";

import * as React from "react";
import {
  Box,
  Container,
  Stack,
  Typography,
  Paper,
  Divider,
  Chip,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import GavelRoundedIcon from "@mui/icons-material/GavelRounded";
import { useI18n } from "i18n/I18nProvider";

type Section = { id: string; titleKey: string; bodyKey: string };

export default function TermsClient({ locale }: { locale: string }) {
  const { t } = useI18n();

  const sections: Section[] = React.useMemo(
    () => [
      { id: "overview", titleKey: "terms_s1_title", bodyKey: "terms_s1_body" },
      { id: "bookings", titleKey: "terms_s2_title", bodyKey: "terms_s2_body" },
      { id: "payments", titleKey: "terms_s3_title", bodyKey: "terms_s3_body" },
      { id: "checkin", titleKey: "terms_s4_title", bodyKey: "terms_s4_body" },
      { id: "house", titleKey: "terms_s5_title", bodyKey: "terms_s5_body" },
      { id: "damage", titleKey: "terms_s6_title", bodyKey: "terms_s6_body" },
      { id: "cancellation", titleKey: "terms_s7_title", bodyKey: "terms_s7_body" },
      { id: "liability", titleKey: "terms_s8_title", bodyKey: "terms_s8_body" },
      { id: "privacy", titleKey: "terms_s9_title", bodyKey: "terms_s9_body" },
      { id: "contact", titleKey: "terms_s10_title", bodyKey: "terms_s10_body" },
    ],
    []
  );

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Box sx={{ py: { xs: 4, md: 6 } }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Stack spacing={1.2} sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip
              icon={<GavelRoundedIcon />}
              label={t("terms_badge")}
              sx={{ borderRadius: 999, fontWeight: 900, bgcolor: "action.selected" }}
            />
            <Chip
              label={t("terms_badge_premium")}
              variant="outlined"
              sx={{ borderRadius: 999, fontWeight: 800 }}
            />
          </Stack>

          <Typography variant="h3" sx={{ fontWeight: 950, letterSpacing: -0.8, lineHeight: 1.05 }}>
            {t("terms_title")}
          </Typography>

          <Typography color="text.secondary" sx={{ maxWidth: 860, lineHeight: 1.7 }}>
            {t("terms_intro")}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {t("terms_last_updated")}
          </Typography>
        </Stack>

        <Stack direction={{ xs: "column", md: "row" }} spacing={2.5} alignItems="flex-start">
          {/* TOC */}
          <Paper
            elevation={0}
            sx={{
              width: { xs: "100%", md: 320 },
              borderRadius: 4,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              position: { md: "sticky" },
              top: { md: 96 },
              overflow: "hidden",
            }}
          >
            <Box sx={{ p: 2 }}>
              <Typography sx={{ fontWeight: 950, mb: 1 }}>
                {t("terms_toc_title")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {t("terms_toc_hint")}
              </Typography>

              <Divider sx={{ mb: 1 }} />

              <List disablePadding>
                {sections.map((s) => (
                  <ListItemButton
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    sx={{ borderRadius: 2, mb: 0.5 }}
                  >
                    <ListItemText
                      primary={t(s.titleKey)}
                      primaryTypographyProps={{ fontWeight: 800 }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          </Paper>

          {/* Content */}
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 4,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              overflow: "hidden",
            }}
          >
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              {sections.map((s, idx) => (
                <Box key={s.id} id={s.id} sx={{ scrollMarginTop: 96 }}>
                  <Typography variant="h6" sx={{ fontWeight: 950, mb: 1 }}>
                    {t(s.titleKey)}
                  </Typography>

                  <Typography
                    color="text.secondary"
                    sx={{ lineHeight: 1.8, whiteSpace: "pre-line" }}
                  >
                    {t(s.bodyKey)}
                  </Typography>

                  {idx !== sections.length - 1 ? <Divider sx={{ my: 3 }} /> : null}
                </Box>
              ))}
            </Box>

            <Divider />

            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                {t("terms_disclaimer")}
              </Typography>
            </Box>
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
}
